const TARGET_SAMPLE_RATE = 16_000;
const PROCESSOR_BUFFER_SIZE = 4096;
const MICROPHONE_PERMISSION_ERROR_MESSAGE =
    "Microphone access is blocked. In your browser's site settings, allow Microphone for Iloris, then reload and try again. If it is already allowed, check your device's microphone privacy settings.";
const MICROPHONE_PERMISSION_ERROR_NAMES = new Set([
    "NotAllowedError",
    "PermissionDeniedError",
]);

export interface PcmCapture {
    stop: () => void;
}

function isMicrophonePermissionDenied(error: unknown): boolean {
    if (typeof error !== "object" || error === null) return false;

    const { name, message } = error as { name?: unknown; message?: unknown };
    if (
        typeof name === "string" &&
        MICROPHONE_PERMISSION_ERROR_NAMES.has(name)
    ) {
        return true;
    }

    return (
        typeof message === "string" &&
        /\b(permission denied|denied permission|not allowed)\b/i.test(message)
    );
}

async function requestMicrophoneStream(): Promise<MediaStream> {
    try {
        return await navigator.mediaDevices.getUserMedia({
            audio: {
                channelCount: 1,
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
            },
        });
    } catch (error) {
        if (isMicrophonePermissionDenied(error)) {
            throw new Error(MICROPHONE_PERMISSION_ERROR_MESSAGE);
        }
        throw error;
    }
}

function encodePcm16(samples: Float32Array, sourceRate: number): string {
    const ratio = sourceRate / TARGET_SAMPLE_RATE;
    const length = Math.max(1, Math.floor(samples.length / ratio));
    const bytes = new Uint8Array(length * 2);
    const view = new DataView(bytes.buffer);
    for (let outputIndex = 0; outputIndex < length; outputIndex += 1) {
        const sourceIndex = Math.min(
            samples.length - 1,
            Math.floor(outputIndex * ratio),
        );
        const sample = Math.max(-1, Math.min(1, samples[sourceIndex] ?? 0));
        view.setInt16(
            outputIndex * 2,
            sample < 0 ? sample * 0x8000 : sample * 0x7fff,
            true,
        );
    }
    let binary = "";
    for (const byte of bytes) binary += String.fromCharCode(byte);
    return btoa(binary);
}

/** Capture starts before token/network initialization; PCM chunks are handed to
 * the caller immediately so it can buffer them only in memory until Scribe opens. */
export async function startPcmCapture(
    onChunk: (base64Pcm: string) => void,
): Promise<PcmCapture> {
    if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Microphone recording is not supported in this browser.");
    }
    let stream: MediaStream | null = null;
    let context: AudioContext | null = null;
    let source: MediaStreamAudioSourceNode | null = null;
    let processor: ScriptProcessorNode | null = null;
    let silentOutput: GainNode | null = null;
    let stopped = false;
    const stop = () => {
        if (stopped) return;
        stopped = true;
        if (processor) processor.onaudioprocess = null;
        source?.disconnect();
        processor?.disconnect();
        silentOutput?.disconnect();
        for (const track of stream?.getTracks() ?? []) track.stop();
        void context?.close();
    };
    try {
        stream = await requestMicrophoneStream();
        const AudioContextClass = window.AudioContext;
        context = new AudioContextClass();
        source = context.createMediaStreamSource(stream);
        processor = context.createScriptProcessor(PROCESSOR_BUFFER_SIZE, 1, 1);
        silentOutput = context.createGain();
        silentOutput.gain.value = 0;
        processor.onaudioprocess = (event) => {
            if (!context) return;
            onChunk(
                encodePcm16(
                    event.inputBuffer.getChannelData(0),
                    context.sampleRate,
                ),
            );
        };
        source.connect(processor);
        processor.connect(silentOutput);
        silentOutput.connect(context.destination);
        if (context.state === "suspended") await context.resume();
        return { stop };
    } catch (error) {
        stop();
        throw error;
    }
}
